<template>
  <div class="bjchy-qrcode-login" style="width: 100%; max-width: 400px; margin: 0 auto; padding: 24px; background: #ffffff; border-radius: 12px; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08); border: 1px solid #e5e7eb; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
    <div v-if="props.title || props.subtitle" style="text-align: center; margin-bottom: 32px;">
      <h2 v-if="props.title" style="margin: 0 0 8px 0; font-size: 24px; font-weight: 600; color: #1f2937; line-height: 1.3;">{{ props.title }}</h2>
      <p v-if="props.subtitle" style="margin: 0; font-size: 14px; color: #6b7280; line-height: 1.4;">{{ props.subtitle }}</p>
    </div>
    
    <div style="display: flex; flex-direction: column; align-items: center; gap: 20px;">
      <!-- 街道配置错误提示 -->
      <div v-if="streetKeyError" style="display: flex; justify-content: center; align-items: center; padding: 40px 20px; background: #fef2f2; border-radius: 12px; border: 2px solid #fecaca; width: 240px; min-height: 240px; box-sizing: border-box;">
        <div style="text-align: center;">
          <div style="width: 48px; height: 48px; background: #fca5a5; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
            <span style="color: #991b1b; font-size: 24px; font-weight: bold;">!</span>
          </div>
          <p style="margin: 0 0 8px 0; font-size: 16px; color: #991b1b; font-weight: 600;">配置错误</p>
          <p style="margin: 0; font-size: 14px; color: #7f1d1d; line-height: 1.4;">{{ streetKeyError }}</p>
        </div>
      </div>
      
      <!-- 二维码显示区域 -->
      <div v-else-if="qrcodeUrl && status !== 'expired'" style="display: flex; justify-content: center; align-items: center; padding: 20px; background: #f9fafb; border-radius: 12px; border: 2px dashed #d1d5db;">
        <img :src="qrcodeUrl" alt="登录二维码" style="width: 200px; height: 200px; border-radius: 8px; display: block;" />
      </div>
      
      <!-- 过期提示区域 -->
      <div v-else-if="status === 'expired'" style="display: flex; justify-content: center; align-items: center; padding: 40px 20px; background: #fef2f2; border-radius: 12px; border: 2px solid #fecaca; width: 240px; height: 240px; box-sizing: border-box;">
        <div style="text-align: center;">
          <p style="margin: 0 0 16px 0; font-size: 16px; color: #991b1b; font-weight: 500;">二维码已过期</p>
          <button @click="handleRefreshQRCode" style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; border: none; padding: 12px 24px; border-radius: 8px; font-size: 14px; font-weight: 500; cursor: pointer; box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);">
            刷新二维码
          </button>
        </div>
      </div>
      
      <!-- 加载中 -->
      <div v-else-if="!streetKeyError" style="display: flex; justify-content: center; align-items: center; flex-direction: column; padding: 40px 20px; background: #f3f4f6; border-radius: 12px; border: 2px dashed #d1d5db; width: 240px; height: 240px; box-sizing: border-box;">
        <div style="width: 24px; height: 24px; border: 3px solid #e5e7eb; border-top: 3px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px;"></div>
        <p style="margin: 0; font-size: 14px; color: #6b7280; text-align: center;">正在生成二维码...</p>
      </div>
      
      <!-- 登录提示 -->
      <div v-if="!streetKeyError" style="display: flex; align-items: center; justify-content: center; gap: 12px; padding: 16px 20px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; min-height: 20px;">
        <span style="font-size: 14px; font-weight: 500; color: #475569; text-align: center; line-height: 1.4;">{{ statusText }}</span>
        <div :style="getStatusIndicatorStyle()"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { request } from '../utils/request.js'
import { detectStreetKey, setCurrentStreet, getStreetConfig, getValidStreetKeys } from '../utils/street.js'
import { generateQRCodeImage } from '../utils/qrcode.js'
import { getSDKConfig } from '../utils/config.js'

/**
 * 有效的街道标识列表 - 从街道映射表动态获取
 */
const VALID_STREET_KEYS = getValidStreetKeys()


/**
 * 检查URL参数中的sessionId并尝试登录
 * 如果sessionId存在且登录成功，则不展示二维码
 */
const checkUrlSessionAndLogin = async () => {
  // 参考getValidStreetKeys()的方法进行获取，只从url里获取
  const urlParams = new URLSearchParams(window.location.search)
  const urlSessionId = urlParams.get('sid')
  
  if (urlSessionId) {
    try {
      sessionId.value = urlSessionId

      console.log('检测到URL中的sessionId，尝试直接登录...')
      const response = await request.post('/auth/statuscheck', { 
        sessionId: urlSessionId, 
        streetKey: validStreetKey.value 
      })
      
      const { status, token, userInfo } = response.data
      
      if (status === 'confirmed' && token) {
        // 登录成功
        console.log('URL sessionId登录成功')
        await handleLoginSuccess(token, userInfo)
        return true // 返回true表示登录成功，不需要展示二维码
      } else {
        console.log('URL sessionId登录失败，状态为:', status)
        return false // 返回false表示需要展示二维码
      }
      
    } catch (error) {
      console.error('URL sessionId登录失败:', error)
      return false // 返回false表示需要展示二维码
    }
  }
  
  return false // 没有sessionId参数，需要展示二维码
}

/**
 * 处理登录成功
 */
const handleLoginSuccess = async (token, userInfo) => {
  // 立即设置登录成功标记，防止后续再启动定时器
  isLoggedIn.value = true
  status.value = 'confirmed'
  
  // 确保停止所有定时器
  stopStatusCheck()
  stopCountdown()
  
  // 保存用户信息和token
  const globalConfig = getSDKConfig()
  localStorage.setItem(globalConfig.tokenKey, token)
  localStorage.setItem(globalConfig.userKey, JSON.stringify(userInfo))
  
  emit('login-success', { token, userInfo, status: 'confirmed' })
}

/**
 * 组件属性
 */
const props = defineProps({
  streetKey: { 
    type: String, 
    default: ''
  },
  title: { 
    type: String, 
    default: '' 
  },
  subtitle: { 
    type: String, 
    default: '' 
  }
})

/**
 * 组件事件
 */
const emit = defineEmits(['login-success', 'login-error', 'status-change'])

// 响应式数据
const qrcodeUrl = ref('')
const sessionId = ref('')
const status = ref('waiting')
const checkTimer = ref(null)
const countdown = ref(60000) // 60秒倒计时，单位毫秒
const countdownTimer = ref(null)
const streetKeyError = ref('')
const isLoggedIn = ref(false) // 登录成功标记

/**
 * 获取有效的街道标识
 */
const validStreetKey = computed(() => {
  // 优先使用props传入的streetKey
  if (props.streetKey) {
    if (VALID_STREET_KEYS.includes(props.streetKey)) {
      return props.streetKey
    } else {  
      streetKeyError.value = `无效的标识`
      return null
    }
  }
  
  // 尝试自动检测streetKey
  const detectedKey = detectStreetKey()
  if (detectedKey && VALID_STREET_KEYS.includes(detectedKey)) {
    return detectedKey
  }
  
  // 无法获取有效的streetKey
  streetKeyError.value = '无法获取配置信息。'
  return null
})

/**
 * 获取街道名称
 */
const streetName = computed(() => {
  if (!validStreetKey.value) {
    return ''
  }
  const streetConfig = getStreetConfig(validStreetKey.value)
  return streetConfig ? streetConfig.name : ''
})

/**
 * 状态文本映射
 */
const statusText = computed(() => {
  const statusMap = {
    waiting: streetName.value ? `请使用微信扫描${streetName.value}二维码` : '请使用微信扫描二维码',
    scanned: '扫描成功，请在手机上确认登录',
    confirmed: '登录成功，正在跳转...',
    expired: '二维码已过期，请重新生成'
  }
  return statusMap[status.value] || '请使用微信扫描二维码'
})

/**
 * 状态指示器样式
 */
const getStatusIndicatorStyle = () => {
  const baseStyle = 'width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; transition: all 0.3s ease;'
  const statusStyles = {
    waiting: 'background: #94a3b8;',
    scanned: 'background: #f59e0b;',
    confirmed: 'background: #10b981;',
    expired: 'background: #ef4444;'
  }
  return baseStyle + ' ' + (statusStyles[status.value] || statusStyles.waiting)
}

/**
 * 生成二维码
 */
const generateQRCode = async () => {
  try {
    // 如果已登录成功，不生成新的二维码
    if (isLoggedIn.value) {
      return
    }
    
    // 检查街道标识是否有效
    if (!validStreetKey.value) {
      emit('login-error', { type: 'invalid-street-key', error: streetKeyError.value })
      return
    }
    
    // 停止之前的状态检查和倒计时
    stopStatusCheck()
    stopCountdown()
    
    // 设置当前街道
    setCurrentStreet(validStreetKey.value)
    
    // 生成会话ID
    sessionId.value = generateSessionId()
    
    // 生成二维码图片
    const qrContent = `https://636c-cloud1-6gb0waw79d5650f1-1311446109.tcb.qcloud.la/webcode/${validStreetKey.value}/t=scanwebcode&t=scanwebcode&id=${sessionId.value}&p=login`
    
    qrcodeUrl.value = await generateQRCodeImage(qrContent, {
      width: 200,
      height: 200
    })
    
    status.value = 'waiting'
    startStatusCheck()
    startCountdown()
    emit('status-change', { status: 'generated', sessionId: sessionId.value, streetKey: validStreetKey.value })
    
  } catch (error) {
    console.error('生成二维码失败:', error)
    stopStatusCheck()
    stopCountdown()
    emit('login-error', { type: 'qrcode-generation-failed', error })
  }
}

/**
 * 开始状态检查
 */
const startStatusCheck = () => {
  // 如果已登录成功，不启动状态检查
  if (isLoggedIn.value) {
    return
  }
  
  stopStatusCheck()
  
  checkTimer.value = setInterval(async () => {
    try {
      // 再次检查登录状态，如果已登录则停止
      if (isLoggedIn.value || !sessionId.value || !validStreetKey.value) {
        stopStatusCheck()
        return
      }
      
      // 使用全局配置的请求实例
      const response = await request.post('/auth/statuscheck', {
        sessionId: sessionId.value,
        streetKey: validStreetKey.value
      })
      
      if (response.success && response.data) {
        if (response.data.status === 'confirmed') {
          await handleLoginSuccess(response.data.token, response.data.userInfo)
        } else if (response.data.status === 'scanned') {
          status.value = 'scanned'
          emit('status-change', { status: 'scanned' })
        }
      } else if (response.message && response.message.includes('过期')) {
        status.value = 'expired'
        stopStatusCheck()
        stopCountdown()
        emit('status-change', { status: 'expired' })
      }
      
    } catch (error) {
      console.error('检查登录状态失败:', error)
    }
  }, 2000)
}

/**
 * 停止状态检查
 */
const stopStatusCheck = () => {
  if (checkTimer.value) {
    clearInterval(checkTimer.value)
    checkTimer.value = null
  }
}

/**
 * 开始倒计时
 */
const startCountdown = () => {
  stopCountdown()
  
  countdown.value = 60000 // 重置为60秒
  
  countdownTimer.value = setInterval(() => {
    countdown.value -= 100 // 每100ms减少
    
    if (countdown.value <= 0) {
      status.value = 'expired'
      stopStatusCheck()
      stopCountdown()
      emit('status-change', { status: 'timeout' })
    }
  }, 100)
}

/**
 * 停止倒计时
 */
const stopCountdown = () => {
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value)
    countdownTimer.value = null
  }
}

/**
 * 处理刷新二维码点击事件
 */
const handleRefreshQRCode = () => {
  if (!validStreetKey.value) {
    return
  }
  
  stopStatusCheck()
  stopCountdown()
  
  setTimeout(() => {
    generateQRCode()
  }, 100)
}

/**
 * 生成随机会话ID
 * @param {number} length - ID长度
 * @returns {string} 随机ID
 */
const generateSessionId = (length = 16) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * 监听streetKey变化，重新验证和生成二维码
 */
watch(() => props.streetKey, () => {
  streetKeyError.value = '' // 重置错误状态
  
  if (validStreetKey.value) {
    stopStatusCheck()
    stopCountdown()
    
    setTimeout(() => {
      generateQRCode()
    }, 100)
  }
}, { immediate: false })

/**
 * 处理页面可见性变化
 */
const handleVisibilityChange = () => {
  if (document.hidden && !isLoggedIn.value) {
    // 页面不可见时暂停定时器
    stopStatusCheck()
    stopCountdown()
  } else if (!document.hidden && !isLoggedIn.value && validStreetKey.value && sessionId.value) {
    // 页面可见时恢复定时器（如果还未登录且有有效的sessionId）
    startStatusCheck()
    startCountdown()
  }
}

// 生命周期钩子
onMounted(async () => {
  if (validStreetKey.value) {
    // 先检查URL参数中的sessionId
    const urlLoginSuccess = await checkUrlSessionAndLogin()
    
    // 如果URL登录没有成功，才生成二维码
    if (!urlLoginSuccess) {
      generateQRCode()
    }
  }
  
  // 监听页面可见性变化
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  // 确保清理所有定时器和事件监听器
  isLoggedIn.value = true // 设置为已登录，防止任何后续操作
  stopStatusCheck()
  stopCountdown()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<style scoped>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
